import PropTypes from 'prop-types';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';

import { RouterLink } from 'src/routes/components';
import { useRouter } from 'src/routes/hook';

import { isInAppLink, renderAnnouncementMarkdown } from './announcement-markdown';

const SEVERITIES = ['info', 'success', 'warning', 'error'];

/** A long message is clamped to this many lines until the reader asks for the rest. */
const COLLAPSED_LINES = 3;
const LINE_HEIGHT = 1.5;

/** In-app paths route inside the app; https:// opens a new tab; mailto: opens the mail client. */
const externalLinkProps = (href) =>
  href.startsWith('mailto:') ? {} : { target: '_blank', rel: 'noopener noreferrer' };

/**
 * The banner's face, shared verbatim with the manager dialog's preview so what a superadmin
 * previews IS what users get — role targeting is honest (superadmins are targeted like everyone
 * else), so the preview is their only first-hand look at a banner aimed elsewhere.
 *
 * The message is formatted (announcement-markdown.js). A message taller than COLLAPSED_LINES is
 * clamped with "Show more", so one long banner cannot push every page down; the overflow is
 * measured rather than guessed from the character count. Error-style banners (maintenance) are
 * never clamped: they are the ones that must be read in full.
 */
export default function AnnouncementAlert({ announcement, onClose, sx }) {
  const router = useRouter();
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  const collapsible = announcement.variant !== 'error';
  const html = useMemo(
    () => renderAnnouncementMarkdown(announcement.message),
    [announcement.message]
  );

  // Measured only while clamped: once expanded the body has no overflow, and re-measuring then
  // would hide the "Show less" that is needed to clamp it again.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || !collapsible) {
      setOverflows(false);
      return undefined;
    }
    if (expanded) return undefined;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [html, collapsible, expanded]);

  const clamped = collapsible && !expanded;

  // Links inside the formatted message are plain anchors; an in-app one must route, not reload.
  const onBodyClick = (event) => {
    const anchor = event.target.closest?.('a');
    const href = anchor?.getAttribute('href');
    if (isInAppLink(href)) {
      event.preventDefault();
      router.push(href);
    }
  };

  const cta = announcement.cta_path;

  return (
    <Alert
      severity={SEVERITIES.includes(announcement.variant) ? announcement.variant : 'info'}
      onClose={announcement.dismissible ? onClose : undefined}
      sx={{ alignItems: announcement.message?.includes('\n') ? 'flex-start' : 'center', ...sx }}
    >
      <AlertTitle sx={{ mb: announcement.message ? 0.25 : 0 }}>{announcement.title}</AlertTitle>

      {!!html && (
        <Box
          ref={bodyRef}
          onClick={onBodyClick}
          sx={{
            lineHeight: LINE_HEIGHT,
            overflowWrap: 'anywhere',
            ...(clamped && {
              maxHeight: `${COLLAPSED_LINES * LINE_HEIGHT}em`,
              overflow: 'hidden',
            }),
            ...(clamped &&
              overflows && {
                maskImage: 'linear-gradient(180deg, #000 55%, transparent)',
                WebkitMaskImage: 'linear-gradient(180deg, #000 55%, transparent)',
              }),
            '& p': { m: 0 },
            '& p + p, & p + ul, & p + ol, & ul + p, & ol + p': { mt: 0.75 },
            '& ul, & ol': { my: 0, pl: 2.5 },
            '& li + li': { mt: 0.5 },
            '& a': { color: 'inherit', fontWeight: 600 },
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}

      {(overflows || expanded) && collapsible && (
        <Button
          size="small"
          color="inherit"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ p: 0, mt: 0.5, minWidth: 0, fontWeight: 600, textTransform: 'none' }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}

      {cta && (
        <Box sx={{ mt: html ? 0.5 : 0 }}>
          {isInAppLink(cta) ? (
            <Link component={RouterLink} href={cta} color="inherit" sx={{ fontWeight: 600 }}>
              {announcement.cta_label}
            </Link>
          ) : (
            <Link href={cta} color="inherit" sx={{ fontWeight: 600 }} {...externalLinkProps(cta)}>
              {announcement.cta_label}
            </Link>
          )}
        </Box>
      )}
    </Alert>
  );
}

AnnouncementAlert.propTypes = {
  announcement: PropTypes.object,
  onClose: PropTypes.func,
  sx: PropTypes.object,
};
